import React, {useEffect, useState} from 'react';
import {Navigate, useNavigate, useSearchParams} from 'react-router-dom';
import {FormField} from '../../forms/formField';
import {DashDropdown} from '../../dropdowns/DashDropdown';
import {MessageCard} from '../../card/messageCard';
import routes from '../../routes/routes';
import {useCategoriesWithTags} from '../../../common/hooks/useCategories';
import {useQueryLocationTags} from '../../../common/hooks/useLocationTags';
import {useOrganizerStore} from '../../../common/stores/useOrganizerStore';
import {useUiStore} from '../../../common/stores/useUiStore';
import {
  createPlace,
  fetchPlaceForEdit,
  PlaceOpeningHour,
  PlacePayload,
  updatePlace,
} from '../../../common/services/places';

const dayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const priceLevels = [
  {label: 'Not set', value: ''},
  {label: '$ · Inexpensive', value: 'PRICE_LEVEL_INEXPENSIVE'},
  {label: '$$ · Moderate', value: 'PRICE_LEVEL_MODERATE'},
  {label: '$$$ · Expensive', value: 'PRICE_LEVEL_EXPENSIVE'},
  {label: '$$$$ · Very expensive', value: 'PRICE_LEVEL_VERY_EXPENSIVE'},
];

interface DayHours {
  isOpen: boolean;
  // "HH:mm", as used by <input type="time">
  opens: string;
  closes: string;
}

interface PlaceFormState {
  name: string;
  description: string;
  phone: string;
  googleMapsUri: string;
  priceLevel: string;
  googleRating: string;
  userRatingCount: string;
  isOperational: boolean;
  street: string;
  streetNumber: string;
  city: string;
  county: string;
  country: string;
  postalCode: string;
  countryIsoCode: string;
  latitude: string;
  longitude: string;
  locationCode?: number;
  tags: number[];
  hours: DayHours[];
  photos: string[];
}

const closedDay: DayHours = {isOpen: false, opens: '09:00', closes: '18:00'};

const emptyForm: PlaceFormState = {
  name: '',
  description: '',
  phone: '',
  googleMapsUri: '',
  priceLevel: '',
  googleRating: '',
  userRatingCount: '',
  isOperational: true,
  street: '',
  streetNumber: '',
  city: '',
  county: '',
  country: 'Romania',
  postalCode: '',
  countryIsoCode: 'RO',
  latitude: '',
  longitude: '',
  locationCode: undefined,
  tags: [],
  hours: dayNames.map(() => ({...closedDay})),
  photos: [],
};

const toFormState = (place: PlacePayload): PlaceFormState => {
  const hours = dayNames.map(() => ({...closedDay}));
  (place.openingHours ?? []).forEach(entry => {
    if (entry.day >= 0 && entry.day < hours.length) {
      hours[entry.day] = {
        isOpen: true,
        opens: (entry.openingTime ?? '').slice(0, 5),
        closes: (entry.closingTime ?? '').slice(0, 5),
      };
    }
  });

  return {
    name: place.name ?? '',
    description: place.description ?? '',
    phone: place.internationalPhoneNumber ?? '',
    googleMapsUri: place.googleMapsUri ?? '',
    priceLevel: place.priceLevel ?? '',
    googleRating: place.googleRating?.toString() ?? '',
    userRatingCount: place.userRatingCount?.toString() ?? '',
    isOperational: place.isOperational ?? true,
    street: place.address?.street ?? '',
    streetNumber: place.address?.streetNumber ?? '',
    city: place.address?.city ?? '',
    county: place.address?.county ?? '',
    country: place.address?.country ?? '',
    postalCode: place.address?.postalCode ?? '',
    countryIsoCode: place.address?.countryIsoCode ?? '',
    latitude: place.location?.latitude?.toString() ?? '',
    longitude: place.location?.longitude?.toString() ?? '',
    locationCode: place.locationCode ?? undefined,
    tags: place.tags ?? [],
    hours,
    photos: (place.photos ?? [])
      .map(photo => photo.photoRoute ?? '')
      .filter(Boolean),
  };
};

// Starts from what the server returned, so fields this form does not show are
// sent back unchanged instead of being wiped.
const toPayload = (
  form: PlaceFormState,
  original: PlacePayload | null,
): PlacePayload => {
  const openingHours: PlaceOpeningHour[] = form.hours
    .map((day, index) => ({day, index}))
    .filter(({day}) => day.isOpen && day.opens && day.closes)
    .map(({day, index}) => ({
      day: index,
      openingTime: `${day.opens}:00`,
      closingTime: `${day.closes}:00`,
    }));

  return {
    ...(original ?? {}),
    name: form.name.trim(),
    description: form.description.trim() || null,
    internationalPhoneNumber: form.phone.trim() || null,
    googleMapsUri: form.googleMapsUri.trim() || null,
    priceLevel: form.priceLevel || null,
    googleRating: form.googleRating === '' ? null : Number(form.googleRating),
    userRatingCount:
      form.userRatingCount === '' ? 0 : Number(form.userRatingCount),
    isOperational: form.isOperational,
    address: {
      ...(original?.address ?? {}),
      street: form.street.trim() || null,
      streetNumber: form.streetNumber.trim() || null,
      city: form.city.trim() || null,
      county: form.county.trim() || null,
      country: form.country.trim() || null,
      postalCode: form.postalCode.trim() || null,
      countryIsoCode: form.countryIsoCode.trim() || null,
    },
    location: {
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
    },
    locationCode: form.locationCode ?? null,
    tags: form.tags,
    openingHours,
    photos: form.photos
      .map(url => url.trim())
      .filter(Boolean)
      .map(photoRoute => ({photoRoute})),
    mapCdnPath: original?.mapCdnPath ?? '',
  };
};

const validate = (form: PlaceFormState): string | null => {
  if (!form.name.trim()) return 'Name is required.';
  if (!form.city.trim()) return 'City is required.';

  const latitude = Number(form.latitude);
  const longitude = Number(form.longitude);
  if (form.latitude === '' || Number.isNaN(latitude) || Math.abs(latitude) > 90)
    return 'Latitude must be a number between -90 and 90.';
  if (
    form.longitude === '' ||
    Number.isNaN(longitude) ||
    Math.abs(longitude) > 180
  )
    return 'Longitude must be a number between -180 and 180.';

  if (form.googleRating !== '') {
    const rating = Number(form.googleRating);
    if (Number.isNaN(rating) || rating < 0 || rating > 5)
      return 'Rating must be between 0 and 5.';
  }
  if (form.userRatingCount !== '') {
    const count = Number(form.userRatingCount);
    if (!Number.isInteger(count) || count < 0)
      return 'Rating count must be a whole number of 0 or more.';
  }

  if (form.tags.length === 0) return 'Select at least one tag.';

  return null;
};

export const PlaceFormScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const placeId = searchParams.get('id');
  const isEdit = Boolean(placeId);

  const organizer = useOrganizerStore(state => state.organizer);
  const setIsLoading = useUiStore(state => state.setIsLoading);
  const {data: categories} = useCategoriesWithTags();
  const {data: locations} = useQueryLocationTags();

  const [form, setForm] = useState<PlaceFormState>(emptyForm);
  const [original, setOriginal] = useState<PlacePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!placeId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchPlaceForEdit(placeId)
      .then(place => {
        if (!cancelled) {
          setOriginal(place);
          setForm(toFormState(place));
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => setIsLoading(false));

    return () => {
      cancelled = true;
    };
  }, [placeId, setIsLoading]);

  // A new place has to belong to an organizer; pick one first, then come back.
  if (!isEdit && !organizer?.id) {
    return (
      <Navigate
        to={routes.organizer}
        state={{next: routes.createPlace}}
        replace
      />
    );
  }

  const setField = <K extends keyof PlaceFormState>(
    key: K,
    value: PlaceFormState[K],
  ) => setForm(current => ({...current, [key]: value}));

  const onText =
    (key: keyof PlaceFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setField(key, e.target.value as never);

  const toggleTag = (code: number) =>
    setField(
      'tags',
      form.tags.includes(code)
        ? form.tags.filter(tag => tag !== code)
        : [...form.tags, code],
    );

  const setDay = (index: number, changes: Partial<DayHours>) =>
    setField(
      'hours',
      form.hours.map((day, i) => (i === index ? {...day, ...changes} : day)),
    );

  const setPhoto = (index: number, url: string) =>
    setField(
      'photos',
      form.photos.map((photo, i) => (i === index ? url : photo)),
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Saving an edit before the place loaded would overwrite it with blanks.
    if (isEdit && !original) {
      setError('The place has not loaded, so it cannot be saved.');
      return;
    }

    const problem = validate(form);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    setIsSaving(true);
    try {
      const payload = toPayload(form, original);
      if (placeId) {
        await updatePlace(placeId, payload);
      } else {
        await createPlace(organizer?.id ?? '', payload);
      }
      navigate(routes.activities);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the place.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={screenStyle}>
      <form onSubmit={handleSubmit} style={cardStyle}>
        <h1 style={{color: '#fff', textAlign: 'center', marginBottom: '8px'}}>
          {isEdit ? 'Edit Place' : 'Add Place'}
        </h1>
        {!isEdit && (
          <p style={hintStyle}>
            For organizer {organizer?.name || organizer?.companyName}
          </p>
        )}

        <h2 style={sectionStyle}>Details</h2>
        <FormField
          label="Name"
          name="name"
          required
          value={form.name}
          onChange={onText('name')}
        />
        <FormField
          label="Description"
          name="description"
          type="textarea"
          value={form.description}
          onChange={onText('description')}
        />
        <FormField
          label="Phone"
          name="phone"
          value={form.phone}
          onChange={onText('phone')}
        />
        <FormField
          label="Google Maps link"
          name="googleMapsUri"
          value={form.googleMapsUri}
          onChange={onText('googleMapsUri')}
        />

        <div style={rowStyle}>
          <div style={{flex: '1 1 140px'}}>
            <label htmlFor="priceLevel" style={labelStyle}>
              Price level
            </label>
            <select
              id="priceLevel"
              value={form.priceLevel}
              onChange={e => setField('priceLevel', e.target.value)}
              style={selectStyle}>
              {/* Keep an unrecognised stored value selectable instead of silently changing it. */}
              {!priceLevels.some(level => level.value === form.priceLevel) && (
                <option value={form.priceLevel}>{form.priceLevel}</option>
              )}
              {priceLevels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="Rating"
              name="googleRating"
              type="number"
              value={form.googleRating}
              onChange={onText('googleRating')}
            />
          </div>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="Rating count"
              name="userRatingCount"
              type="number"
              value={form.userRatingCount}
              onChange={onText('userRatingCount')}
            />
          </div>
        </div>

        {isEdit && (
          <label style={checkboxStyle}>
            <input
              type="checkbox"
              checked={form.isOperational}
              onChange={e => setField('isOperational', e.target.checked)}
            />
            Operational (open for business)
          </label>
        )}

        <h2 style={sectionStyle}>Address</h2>
        <div style={rowStyle}>
          <div style={{flex: '3 1 200px'}}>
            <FormField
              label="Street"
              name="street"
              value={form.street}
              onChange={onText('street')}
            />
          </div>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="Number"
              name="streetNumber"
              value={form.streetNumber}
              onChange={onText('streetNumber')}
            />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="City"
              name="city"
              required
              value={form.city}
              onChange={onText('city')}
            />
          </div>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="County"
              name="county"
              value={form.county}
              onChange={onText('county')}
            />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={{flex: '2 1 200px'}}>
            <FormField
              label="Country"
              name="country"
              value={form.country}
              onChange={onText('country')}
            />
          </div>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="Country code"
              name="countryIsoCode"
              value={form.countryIsoCode}
              onChange={onText('countryIsoCode')}
            />
          </div>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="Postal code"
              name="postalCode"
              value={form.postalCode}
              onChange={onText('postalCode')}
            />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="Latitude"
              name="latitude"
              required
              value={form.latitude}
              onChange={onText('latitude')}
            />
          </div>
          <div style={{flex: '1 1 140px'}}>
            <FormField
              label="Longitude"
              name="longitude"
              required
              value={form.longitude}
              onChange={onText('longitude')}
            />
          </div>
        </div>

        <label style={labelStyle}>App location</label>
        <div style={{margin: '6px 0 20px'}}>
          <DashDropdown
            options={(locations ?? []).map(location => ({
              code: location.code ?? 0,
              name: location.name ?? '',
            }))}
            selectedValue={form.locationCode}
            onChange={({code}) => setField('locationCode', code)}
            placeholder="Derived from the county when left empty"
          />
        </div>

        <h2 style={sectionStyle}>
          Tags <span style={countStyle}>{form.tags.length} selected</span>
        </h2>
        {(categories ?? []).map(category => (
          <div key={category.code} style={{marginBottom: '14px'}}>
            <div style={labelStyle}>{category.name}</div>
            <div style={chipsStyle}>
              {category.tags.map(tag => {
                const selected = form.tags.includes(tag.code);
                return (
                  <button
                    key={tag.code}
                    type="button"
                    onClick={() => toggleTag(tag.code)}
                    aria-pressed={selected}
                    style={selected ? activeChipStyle : chipStyle}>
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <h2 style={sectionStyle}>Opening hours</h2>
        {form.hours.map((day, index) => (
          <div key={dayNames[index]} style={dayRowStyle}>
            <label style={{...checkboxStyle, width: '130px', margin: 0}}>
              <input
                type="checkbox"
                checked={day.isOpen}
                onChange={e => setDay(index, {isOpen: e.target.checked})}
              />
              {dayNames[index]}
            </label>
            {day.isOpen ? (
              <>
                <input
                  type="time"
                  aria-label={`${dayNames[index]} opens`}
                  value={day.opens}
                  onChange={e => setDay(index, {opens: e.target.value})}
                  style={timeStyle}
                />
                <span style={{color: '#777'}}>to</span>
                <input
                  type="time"
                  aria-label={`${dayNames[index]} closes`}
                  value={day.closes}
                  onChange={e => setDay(index, {closes: e.target.value})}
                  style={timeStyle}
                />
              </>
            ) : (
              <span style={{color: '#777'}}>Closed</span>
            )}
          </div>
        ))}

        <h2 style={sectionStyle}>Photos</h2>
        {form.photos.map((photo, index) => (
          <div key={index} style={photoRowStyle}>
            {photo ? (
              <img src={photo} alt="" style={photoPreviewStyle} />
            ) : (
              <div style={photoPreviewStyle} />
            )}
            <input
              aria-label={`Photo ${index + 1} URL`}
              placeholder="https://..."
              value={photo}
              onChange={e => setPhoto(index, e.target.value)}
              style={{...timeStyle, flex: 1}}
            />
            <button
              type="button"
              onClick={() =>
                setField(
                  'photos',
                  form.photos.filter((_, i) => i !== index),
                )
              }
              style={chipStyle}>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setField('photos', [...form.photos, ''])}
          style={chipStyle}>
          Add photo URL
        </button>

        <MessageCard type={error ? 'error' : null} message={error} />

        <div style={actionsStyle}>
          <button
            type="button"
            onClick={() => navigate(routes.activities)}
            style={chipStyle}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving || (isEdit && !original)}
            style={
              isSaving || (isEdit && !original)
                ? {...submitStyle, opacity: 0.5, cursor: 'not-allowed'}
                : submitStyle
            }>
            {isSaving ? 'Saving...' : isEdit ? 'Save changes' : 'Create place'}
          </button>
        </div>
      </form>
    </div>
  );
};

const screenStyle: React.CSSProperties = {
  backgroundColor: '#1a1a1a',
  minHeight: '100vh',
  padding: '40px 20px',
};

const cardStyle: React.CSSProperties = {
  maxWidth: '700px',
  margin: '0 auto',
  padding: '32px',
  backgroundColor: '#353535',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
};

const sectionStyle: React.CSSProperties = {
  color: '#fff',
  fontSize: '1.1rem',
  margin: '28px 0 16px',
  paddingBottom: '8px',
  borderBottom: '1px solid #4a4a4a',
};

const hintStyle: React.CSSProperties = {
  color: '#aaa',
  textAlign: 'center',
  margin: 0,
};

const countStyle: React.CSSProperties = {
  color: '#aaa',
  fontSize: '0.8rem',
  fontWeight: 'normal',
  marginLeft: '8px',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#aaa',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

// Wraps on narrow screens instead of squeezing the fields.
const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: '12px',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  height: '43px',
  padding: '0 12px',
  marginTop: '6px',
  marginBottom: '20px',
  borderRadius: '8px',
  border: '1px solid #333',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  fontSize: '0.8rem',
  boxSizing: 'border-box',
};

const checkboxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: '#fff',
  fontSize: '0.85rem',
  marginBottom: '12px',
  cursor: 'pointer',
};

const chipsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: '8px',
};

const chipStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: '999px',
  border: '1px solid #4a4a4a',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

const activeChipStyle: React.CSSProperties = {
  ...chipStyle,
  border: '1px solid #daff00',
  backgroundColor: '#daff00',
  color: '#1a1a1a',
  fontWeight: 'bold',
};

const dayRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minHeight: '40px',
};

const timeStyle: React.CSSProperties = {
  height: '36px',
  padding: '0 10px',
  borderRadius: '8px',
  border: '1px solid #333',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  fontSize: '0.8rem',
  boxSizing: 'border-box',
};

const photoRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '10px',
};

const photoPreviewStyle: React.CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  objectFit: 'cover',
  backgroundColor: '#2a2a2a',
  flexShrink: 0,
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '28px',
};

const submitStyle: React.CSSProperties = {
  padding: '10px 22px',
  borderRadius: '8px',
  border: '1px solid #daff00',
  backgroundColor: '#daff00',
  color: '#1a1a1a',
  fontWeight: 'bold',
  cursor: 'pointer',
};
